import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import LoadingButton from '@mui/lab/LoadingButton';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    FormHelperText,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import * as yup from 'yup';
import { useHttpContext } from '@/common/hooks/HttpContext';
import { toasts } from '@/common/toasts';
import { EmailName, OrganizationAdmin, SessionInputs } from '@/common/types';
import { OrganizationVisitReasonsContainer } from '@/components/Organization/AdminOrganization/VisitReasons/OrganizationVisitReasonsContainer';
import { resultIsError } from '@/services/HttpService';

const schema = yup.object().shape({
    visitReasonIds: yup.array().of(yup.number().required()).required(),
    maxVisitReasonsAllowed: yup.number().typeError('Max visit reasons is required').integer().min(1).max(3).required(),
    supportsAssessmentFrameCapture: yup.boolean().required(),
    firstScanLoginEmail: yup.string().nullable().defined(),
    requiredSessionInputs: yup
        .array()
        .of(yup.string().required())
        .required()
        .test(
            'visit-reasons-required',
            'Visit Reasons cannot be a required session input when no visit reasons are selected',
            (value, context) =>
                !value?.includes(SessionInputs.visitReasons) || context.parent.visitReasonIds?.length > 0,
        ),
});

interface FormValues {
    visitReasonIds: number[];
    maxVisitReasonsAllowed: number;
    supportsAssessmentFrameCapture: boolean;
    firstScanLoginEmail: EmailName | null;
    requiredSessionInputs: SessionInputs[];
}

const SESSION_INPUT_LABELS: Record<SessionInputs, string> = {
    [SessionInputs.participantUid]: 'Participant UID',
    [SessionInputs.visitReasons]: 'Visit Reasons',
};

interface AdminOrganizationSettingsFormProps {
    organization: OrganizationAdmin;
}

export const AdminOrganizationSettingsForm = ({ organization }: AdminOrganizationSettingsFormProps) => {
    const { httpService } = useHttpContext();
    const { successToast, errorToast } = toasts;

    const {
        control,
        handleSubmit,
        reset,
        formState: { isDirty, dirtyFields, isSubmitting },
    } = useForm<FormValues>({
        resolver: yupResolver(schema),
        defaultValues: {
            visitReasonIds: organization.visitReasons.map((vr) => vr.id),
            maxVisitReasonsAllowed: organization.maxVisitReasonsAllowed,
            supportsAssessmentFrameCapture: organization.supportsAssessmentFrameCapture,
            firstScanLoginEmail: organization.firstScanLoginEmail,
            requiredSessionInputs: organization.requiredSessionInputs,
        },
    });

    const onSubmit = async (data: FormValues) => {
        const results = await Promise.all([
            dirtyFields.visitReasonIds
                ? httpService.setOrganizationVisitReasons(organization.id, data.visitReasonIds)
                : Promise.resolve(null),
            httpService.updateOrganizationAdmin(organization.id, {
                supportsAssessmentFrameCapture: data.supportsAssessmentFrameCapture,
                firstScanLoginEmail: data.firstScanLoginEmail,
                requiredSessionInputs: data.requiredSessionInputs,
                maxVisitReasonsAllowed: data.maxVisitReasonsAllowed,
            }),
        ]);
        const hasError = results.some((r) => r !== null && resultIsError(r));
        if (hasError) {
            errorToast('Failed to save changes');
        } else {
            reset(data);
            successToast('Changes saved');
        }
    };

    return (
        <form
            onSubmit={handleSubmit(onSubmit)}
            style={{ display: 'flex', flexDirection: 'column', gap: '16px', width: 425 }}
        >
            <Controller
                control={control}
                name="visitReasonIds"
                render={({ field: { value, onChange } }) => (
                    <OrganizationVisitReasonsContainer selectedIds={value} onChange={onChange} />
                )}
            />
            <Controller
                control={control}
                name="maxVisitReasonsAllowed"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <TextField
                        type="number"
                        label="Max Visit Reasons Allowed"
                        fullWidth
                        value={value}
                        onChange={(e) => onChange(e.target.value === '' ? '' : Number(e.target.value))}
                        inputProps={{ min: 1, max: 3, step: 1 }}
                        error={!!error}
                        helperText={error?.message}
                    />
                )}
            />
            <Controller
                control={control}
                name="firstScanLoginEmail"
                render={({ field: { value, onChange } }) => (
                    <FormControl fullWidth>
                        <InputLabel id="first-scan-login-email-label">Scan First Login Email</InputLabel>
                        <Select
                            labelId="first-scan-login-email-label"
                            label="Scan First Login Email"
                            value={value ?? ''}
                            onChange={(e) => onChange(e.target.value || null)}
                        >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value={EmailName.ScanStandardWelcome}>Scan Standard Welcome</MenuItem>
                        </Select>
                    </FormControl>
                )}
            />
            <Controller
                control={control}
                name="requiredSessionInputs"
                render={({ field: { value, onChange }, fieldState: { error } }) => (
                    <FormControl fullWidth error={!!error}>
                        <InputLabel id="required-session-inputs-label">Required Session Inputs</InputLabel>
                        <Select
                            labelId="required-session-inputs-label"
                            label="Required Session Inputs"
                            multiple
                            value={value}
                            onChange={(e) => onChange(e.target.value)}
                            renderValue={(selected) => selected.map((s) => SESSION_INPUT_LABELS[s]).join(', ')}
                        >
                            {Object.values(SessionInputs).map((sessionInput) => (
                                <MenuItem key={sessionInput} value={sessionInput}>
                                    <Checkbox checked={value.includes(sessionInput)} />
                                    <ListItemText primary={SESSION_INPUT_LABELS[sessionInput]} />
                                </MenuItem>
                            ))}
                        </Select>
                        {error && <FormHelperText>{error.message}</FormHelperText>}
                    </FormControl>
                )}
            />
            <Controller
                control={control}
                name="supportsAssessmentFrameCapture"
                render={({ field: { value, onChange } }) => (
                    <FormControlLabel
                        label="Supports Assessment Frame Capture"
                        control={<Checkbox checked={value} onChange={(e) => onChange(e.target.checked)} />}
                    />
                )}
            />
            <div>
                <LoadingButton type="submit" variant="primary" disabled={!isDirty} loading={isSubmitting}>
                    Save
                </LoadingButton>
            </div>
        </form>
    );
};
